import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Empty, Row, Space, Spin, Tag, Typography } from 'antd';
import { CheckCircleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMyDeliveryAssignments } from '@/hooks/useDelivery';
import DeliveryCompletionModal from '@/components/sales/DeliveryCompletionModal';
import type { MyDeliveryAssignment } from '@/services/delivery.service';
import { getDeliveryStatusTagColor } from '@/services/delivery.service';

const { Title, Text } = Typography;

const MyDeliveriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { data = [], isLoading, refetch } = useMyDeliveryAssignments();
  const [active, setActive] = useState<MyDeliveryAssignment | null>(null);

  const sorted = useMemo(
    () =>
      [...data].sort((a, b) => {
        const aDone = a.delivery_status === 'Delivered' ? 1 : 0;
        const bDone = b.delivery_status === 'Delivered' ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        return dayjs(b.delivery_date).valueOf() - dayjs(a.delivery_date).valueOf();
      }),
    [data]
  );

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            My Deliveries
          </Title>
          <Text type="secondary">Assigned execution board for delivery completion with proof.</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Space>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : sorted.length === 0 ? (
        <Card>
          <Empty description="No assigned in-transit deliveries." />
        </Card>
      ) : (
        <Row gutter={[12, 12]}>
          {sorted.map((row) => (
            <Col key={row.id} xs={24} sm={24} md={12} lg={8}>
              <Card
                title={
                  <Space>
                    <Text strong>{row.delivery_note_number}</Text>
                    <Tag color={getDeliveryStatusTagColor(row.delivery_status)}>{row.delivery_status}</Tag>
                  </Space>
                }
                extra={<Text type="secondary">{dayjs(row.delivery_date).format('DD MMM YYYY')}</Text>}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Text>
                    <strong>Customer:</strong> {row.customer_name || '-'}
                  </Text>
                  <Text>
                    <strong>Address:</strong> {row.delivery_address || '-'}
                  </Text>
                  <Text>
                    <strong>Order:</strong> {row.sales_order_number || '-'}
                  </Text>
                  <Text>
                    <strong>Items:</strong> {row.item_count} | <strong>Proof:</strong> {row.proof_count}
                  </Text>
                  {row.special_instructions ? (
                    <Text type="secondary">
                      <strong>Instructions:</strong> {row.special_instructions}
                    </Text>
                  ) : null}
                  <Space style={{ marginTop: 8 }} wrap>
                    <Button icon={<EyeOutlined />} onClick={() => navigate(`/growth/delivery-notes/${row.id}`)}>
                      Open
                    </Button>
                    {row.delivery_status === 'In Transit' ? (
                      <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setActive(row)}>
                        Mark delivered
                      </Button>
                    ) : null}
                  </Space>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {active ? (
        <DeliveryCompletionModal
          open={!!active}
          deliveryId={active.id}
          deliveryNumber={active.delivery_note_number}
          customerName={active.customer_name}
          deliveryAddress={active.delivery_address}
          instructions={active.special_instructions}
          onClose={() => setActive(null)}
        />
      ) : null}
    </div>
  );
};

export default MyDeliveriesPage;
